import React, { useState, useRef } from 'react';
import { useFecharAoClicarFora } from '../utils/fecharAoClicarFora';
import './ThemePicker.css';
import { useFechavelComVoltar } from '../contexts/VoltarDoCelular';

export interface Theme {
  id: string;
  name: string;
  p: string;    // primary
  pd: string;   // primary dark
  pb: string;   // primary bright
  pl: string;   // primary light bg
  pm: string;   // primary muted bg
  bgFrom: string;
  bgTo: string;
  /**
   * O fundo da tela de LOGIN — fundo e saturado, não o `bgFrom` claro.
   *
   * ── Por que é escolhido e não calculado ───────────────────────────────
   *
   * A primeira versão derivava: o acento em `rgba()` sobre um quase-preto.
   * Ficou pastel. Misturar cor com preto DESSATURA, e o resultado era um
   * lilás acinzentado — "parece de produto de beleza", foi o relato.
   *
   * `#2d1b69` não é uma versão escura de `#6d4fc2`: é um índigo escolhido,
   * com saturação alta para o quanto é escuro. Nenhuma conta chega nele
   * partindo do acento.
   *
   * Então são valores, como `bgFrom` já era. Os oito primeiros mantêm a
   * mesma saturação e luminosidade do original em cada matiz; `slate` e
   * `dark` são neutros na mão — o acento deles puxa para azul, e um tema
   * chamado "Cinza" não pode ter porta azul.
   */
  loginFrom: string;
  loginTo: string;
}

export const THEMES: Theme[] = [
  { id: 'violet',  name: 'Roxo',       p: '#6d4fc2', pd: '#4e3594', pb: '#9b7de0', pl: '#eee9fa', pm: '#f6f3fd', bgFrom: '#c0b0e8', bgTo: '#d8ccf4', loginFrom: '#2d1b69', loginTo: '#1a0f3e' },
  { id: 'indigo',  name: 'Índigo',     p: '#4a5db5', pd: '#3a4a96', pb: '#7b8dd4', pl: '#e4e8f8', pm: '#f0f2fc', bgFrom: '#b0bce4', bgTo: '#c8d0ee', loginFrom: '#1b2969', loginTo: '#0f173e' },
  { id: 'blue',    name: 'Azul',       p: '#3a6db8', pd: '#2d5899', pb: '#6a9fd4', pl: '#dce8f5', pm: '#eff5fb', bgFrom: '#a8c4e4', bgTo: '#c4d8ef', loginFrom: '#1b3b69', loginTo: '#0f223e' },
  { id: 'teal',    name: 'Verde Água', p: '#2a8a9e', pd: '#1f6f7e', pb: '#56b5c8', pl: '#d0f0f6', pm: '#eafafe', bgFrom: '#90d0dc', bgTo: '#b8e4ea', loginFrom: '#1b5c69', loginTo: '#0f363e' },
  { id: 'emerald', name: 'Verde',      p: '#2a8a6a', pd: '#1f6e54', pb: '#5bb897', pl: '#d0f0e4', pm: '#eafaf3', bgFrom: '#90d4bc', bgTo: '#b8e8d4', loginFrom: '#1b694f', loginTo: '#0f3e2e' },
  { id: 'rose',    name: 'Rosa',       p: '#c24a6a', pd: '#9e3352', pb: '#e07a98', pl: '#f8dde4', pm: '#fdf0f3', bgFrom: '#e4aabb', bgTo: '#eec8d0', loginFrom: '#691b30', loginTo: '#3e0f1c' },
  { id: 'orange',  name: 'Laranja',    p: '#c4682a', pd: '#a05020', pb: '#e09060', pl: '#f8e8d4', pm: '#fdf4ea', bgFrom: '#e4c098', bgTo: '#eedad8', loginFrom: '#693a1b', loginTo: '#3e220f' },
  { id: 'pink',    name: 'Pink',       p: '#c2408a', pd: '#9e2870', pb: '#e07ab4', pl: '#f8ddef', pm: '#fdf0f8', bgFrom: '#e4aace', bgTo: '#eec8df', loginFrom: '#691b47', loginTo: '#3e0f2a' },
  { id: 'slate',   name: 'Cinza',      p: '#475569', pd: '#334155', pb: '#94a3b8', pl: '#f1f5f9', pm: '#f8fafc', bgFrom: '#c8d4e0', bgTo: '#dde6ef', loginFrom: '#2b3441', loginTo: '#171c25' },
  { id: 'dark',    name: 'Preto',      p: '#1f2937', pd: '#111827', pb: '#4b5563', pl: '#f3f4f6', pm: '#f9fafb', bgFrom: '#8a9ab0', bgTo: '#b0bcc8', loginFrom: '#23262d', loginTo: '#101216' },
];

function hexToRgb(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r}, ${g}, ${b}`;
}

/** Mistura duas cores hex. `t` vai de 0 (a) a 1 (b). */
function mistura(a: string, b: string, t: number): string {
  const p = (h: string) => [1, 3, 5].map(i => parseInt(h.slice(i, i + 2), 16));
  const [r1, g1, b1] = p(a), [r2, g2, b2] = p(b);
  const c = (x: number, y: number) => Math.round(x + (y - x) * t).toString(16).padStart(2, '0');
  return `#${c(r1, r2)}${c(g1, g2)}${c(b1, b2)}`;
}

/** Luminância relativa do WCAG. */
function luminancia(hex: string): number {
  const p = [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16) / 255);
  const f = (v: number) => (v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4));
  return 0.2126 * f(p[0]) + 0.7152 * f(p[1]) + 0.0722 * f(p[2]);
}

export function contraste(a: string, b: string): number {
  const [x, y] = [luminancia(a), luminancia(b)].sort((m, n) => n - m);
  return (x + 0.05) / (y + 0.05);
}

/**
 * O tom do subtítulo da marca — cinco voltas, e a quinta é dela também.
 *
 * `#e5e3e3` foi escolha dela, três vezes: o cinza de corpo era "escuro
 * demais", o AA de 4,5 era "pra deixar claro pra não contrastar", e a
 * derivação mirando 2,2 também saiu.
 *
 * ── A quinta volta: ela viu a consequência e mudou de lado ──────────────
 *
 * "Texto de baixo tá muito claro, em algumas cores não dá leitura." A
 * primeira tentativa foi dar aos outros nove temas a mesma discrição do Roxo
 * (1,55) — e ela respondeu com a própria tela na frente: no Roxo também não
 * lê. Medido, `#e5e3e3` dá 1,55:1 lá, 1,18 no Cinza e 1,33 no Verde.
 *
 * Perguntado com os números na mesa, o pedido foi explícito: escurecer até
 * dar leitura. O alvo passou a ser 4,5:1 — o mínimo do WCAG para texto
 * normal — em TODOS os temas, o dela inclusive.
 *
 * Isto contraria as três escolhas anteriores, e é de propósito: as três foram
 * feitas sem ver o número de cada fundo, e esta foi feita olhando todos.
 * Escolha nova, com mais informação, ganha da antiga.
 *
 * ── Por que derivado, e não um valor só ─────────────────────────────────
 *
 * Porque o valor único que serve ao pior fundo fica escuro demais nos outros
 * nove — que foi exatamente a reclamação da primeira volta. A derivação dá a
 * cada tema o tom MAIS CLARO que ainda lê no fundo dele.
 */
export const SUBTITULO_ESCOLHIDO = '#e5e3e3';

/** O mínimo do WCAG para texto normal. */
export const ALVO_DO_SUBTITULO = 4.5;

/**
 * Para onde a frase escurece: o acento do tema, aprofundado.
 *
 * ── Por que não em direção ao PRETO ─────────────────────────────────────
 *
 * A primeira versão misturava com `#000000`, e o resultado era cinza neutro —
 * `#494949` no Roxo. Pedido dela, na hora: "o subtítulo nesse caso não pode
 * deixar meio roxo em tema claro". Está certa: a frase é da MARCA, e cinza
 * neutro a desliga dela.
 *
 * ── E por que não o `pd` puro ───────────────────────────────────────────
 *
 * Porque ele não é escuro o bastante. Medido, misturar até o `pd` não alcança
 * 4,5 em sete dos dez temas — o Verde Água para em 3,37. Aprofundar com 45%
 * de preto dá um destino que ainda carrega o tom: no Roxo a frase termina em
 * `#4e436d`, um roxo abafado, e não num cinza.
 *
 * Nos temas Cinza e Preto o resultado sai quase neutro, e é o certo: o acento
 * deles já é cinza.
 */
const RUMO_DO_SUBTITULO = 0.45;

export const subtituloDoTema = (theme: Theme): string => {
  const destino = mistura(theme.pd, '#000000', RUMO_DO_SUBTITULO);
  /*
   * Escurece em passos de 1%, e para no primeiro que alcança. Assim o
   * resultado é sempre o tom MAIS CLARO que serve àquele fundo — nunca mais
   * escuro do que o necessário. É o que separa isto de cravar um cinza: o
   * "escuro demais" que ela recusou na primeira volta era um valor único
   * dimensionado para o pior fundo e aplicado nos dez.
   */
  for (let t = 0; t <= 100; t++) {
    const cor = mistura(SUBTITULO_ESCOLHIDO, destino, t / 100);
    if (contraste(cor, theme.bgFrom) >= ALVO_DO_SUBTITULO) return cor;
  }
  return destino;
};

/* ── O selo "da casa" ───────────────────────────────────────────────────── */

/**
 * O fundo dos cartões, nos dois modos.
 *
 * Copiado de `--s-card` no `index.css`, porque o cálculo acontece em JS antes
 * de qualquer coisa ser pintada e não há como ler a folha aqui.
 * `seloDoTema.test.ts` compara os dois arquivos e falha se um mudar sem o
 * outro — a mesma trava que a prova da casa usa contra o `server.ts`.
 */
export const FUNDO_DO_CARTAO = { claro: '#ffffff', escuro: '#1c1c23' };

export const ALVO_DO_SELO = 4.5;

/**
 * A cor do texto do selo "da casa", por tema e por modo.
 *
 * ── Por que não `--t-p`, que era o óbvio ────────────────────────────────
 *
 * Foi o que eu escrevi primeiro, e medi UM caso — Roxo no claro, 5,92. Passou,
 * e eu quase parei ali. Medindo os vinte, `--t-p` reprova em TREZE: todos os
 * dez no escuro, e Verde Água, Verde e Laranja também no claro.
 *
 * O acento é dimensionado para preencher, não para escrever. Um texto de
 * 0,68rem precisa de 4,5:1, e o `p` de cada tema não foi escolhido para isso.
 *
 * ── De onde cada modo parte ─────────────────────────────────────────────
 *
 * Do `pd` no claro e do `pb` no escuro — as variantes que o tema já tem para
 * exatamente isto. Só isso já resolve 19 dos 20 casos.
 *
 * O que sobra é o Preto no escuro: o `pb` dele é um cinza escuro sobre um
 * cartão quase preto, e dá 2,24. Por isso a cor não é lida da paleta e sim
 * PROCURADA: clareia (ou escurece) em passos de 1% e para no primeiro que
 * alcança o alvo. Assim o resultado é sempre o tom mais próximo do acento que
 * ainda serve — nunca um valor único dimensionado para o pior tema e aplicado
 * nos dez, que é o erro que o subtítulo já cometeu uma vez aqui.
 */
export const seloDoTema = (theme: Theme, dark: boolean): string => {
  const fundo = dark ? FUNDO_DO_CARTAO.escuro : FUNDO_DO_CARTAO.claro;
  const partida = dark ? theme.pb : theme.pd;
  const destino = dark ? '#ffffff' : '#000000';
  for (let t = 0; t <= 100; t++) {
    const cor = mistura(partida, destino, t / 100);
    if (contraste(cor, fundo) >= ALVO_DO_SELO) return cor;
  }
  return destino;
};

/**
 * Aplica o tema — cor e claro/escuro juntos, e tem que ser junto.
 *
 * Os tokens de tema (`--t-pl`, `--t-pm`, `--t-bg-*`) são gravados como estilo
 * INLINE no `:root`, e inline vence folha de estilo. Se o modo escuro fosse só
 * um bloco `[data-theme='dark']` no CSS, esses quatro continuariam claros e a
 * página ficaria com fundo lilás claro embaixo de cards escuros.
 *
 * As superfícies tingidas de acento são calculadas a partir da MESMA cor que
 * ela escolheu, misturada com preto em vez de branco — a escolha dela continua
 * valendo no escuro em vez de virar cinza neutro.
 *
 * O acento vira `theme.pb` (a variante clara) no escuro: `theme.p` foi
 * escolhido para contrastar com branco, e sobre superfície escura ele afunda.
 */
/**
 * Onde a escolha de tema fica guardada.
 *
 * Estas chaves moravam em `App.tsx`, e a leitura delas também. Mudaram de casa
 * quando a tela de LOGIN passou a seguir o tema: o login renderiza em `App()`,
 * e o tema era aplicado em `AppContent()` — que nem montou ainda. A tela de
 * entrada nunca via a cor que ela escolheu, nem o modo escuro.
 *
 * Ler as chaves em dois lugares seria a armadilha 4. Então a leitura é uma só,
 * aqui, junto de `THEMES` e `applyTheme`.
 */
export const THEME_KEY = 'wallet-theme';
export const DARK_KEY = 'wallet-dark';

/**
 * O tema salvo, ou o padrão.
 *
 * Sem preferência de claro/escuro salva, abre no escuro (22/10): é o tema da
 * demonstração e da página de apresentação, e quem chega por elas deve
 * encontrar o mesmo app. Antes seguia o sistema operacional.
 */
export const temaSalvo = (): { theme: Theme; dark: boolean } => {
  const theme = THEMES.find(t => t.id === localStorage.getItem(THEME_KEY)) ?? THEMES[0];
  const salvo = localStorage.getItem(DARK_KEY);
  return {
    theme,
    dark: salvo !== null ? salvo === 'true' : true,
  };
};

/**
 * Aplica o que está salvo. Idempotente — chamar duas vezes com o mesmo valor
 * escreve os mesmos inline styles.
 */
export const aplicarTemaSalvo = (): { theme: Theme; dark: boolean } => {
  const t = temaSalvo();
  applyTheme(t.theme, t.dark);
  return t;
};

export function applyTheme(theme: Theme, dark = false) {
  const r = document.documentElement.style;
  const acento = dark ? theme.pb : theme.p;

  r.setProperty('--t-p',       acento);
  r.setProperty('--t-p-rgb',   hexToRgb(acento));
  /**
   * `--t-pd` é o acento com MAIS ênfase que `--t-p` — e "mais ênfase" muda de
   * direção com o fundo. No claro é mais escuro; no escuro é mais claro.
   *
   * Apontá-lo para `theme.p` no escuro (o violeta médio) parecia simétrico e
   * não é: ele é cor de TEXTO em 13 lugares, e o saldo da faixa caía para
   * 2,53:1 — o número mais importante da tela, quase invisível. Como fundo de
   * hover era igualmente errado: escurecer sobre escuro some, e a tinta
   * `--t-on-p` reprovava em cima dele.
   */
  r.setProperty('--t-pd',      dark ? mistura(theme.pb, '#ffffff', 0.25) : theme.pd);
  r.setProperty('--t-pb',      theme.pb);

  /**
   * A tinta que vai SOBRE o acento.
   *
   * No claro o acento é escuro e carrega branco. No escuro ele vira a variante
   * clara do tema — e branco sobre lilás claro dá 3,28:1, que reprova o AA.
   * A mesma regra do verde do WhatsApp: superfície clara pede tinta escura.
   */
  r.setProperty('--t-on-p', dark ? '#16121f' : '#ffffff');

  if (dark) {
    r.setProperty('--t-pl',      mistura('#1c1c23', theme.p, 0.16));
    r.setProperty('--t-pm',      mistura('#16161c', theme.p, 0.10));
    r.setProperty('--t-bg-from', mistura('#0e0e13', theme.p, 0.10));
    r.setProperty('--t-bg-to',   mistura('#15151c', theme.p, 0.14));
  } else {
    r.setProperty('--t-pl',      theme.pl);
    r.setProperty('--t-pm',      theme.pm);
    r.setProperty('--t-bg-from', theme.bgFrom);
    r.setProperty('--t-bg-to',   theme.bgTo);
  }

  /*
   * O subtítulo da marca — só no CLARO, e ancorado no valor dela.
   *
   * No claro a frase mora sobre o gradiente da página, que vai de lilás claro
   * a cinza-azulado médio conforme o tema: um valor único ou some nos fundos
   * claros ou some no Preto. `subtituloDoTema` parte do `#e5e3e3` dela e só
   * escurece o quanto cada fundo exige para alcançar a MESMA discrição que
   * ela aprovou no Roxo.
   *
   * No escuro não se mexe — ela mandou não mexer, e não precisa: os dez
   * fundos escuros são quase pretos e o token do `index.css` dá 7,07 no pior
   * deles. A propriedade é REMOVIDA em vez de escrita, senão o valor inline
   * do claro sobreviveria à troca para o escuro e venceria a folha.
   */
  if (dark) r.removeProperty('--s-subtitulo');
  else r.setProperty('--s-subtitulo', subtituloDoTema(theme));

  /*
   * O selo é escrito nos DOIS modos, ao contrário do subtítulo.
   *
   * O subtítulo só precisa de ajuste no claro — os dez fundos escuros são
   * quase pretos e o token do `index.css` já resolve. Aqui o problema existe
   * dos dois lados: o acento reprova em contraste no claro em três temas e no
   * escuro em todos os dez.
   */
  r.setProperty('--s-selo', seloDoTema(theme, dark));


  /*
   * O fundo do login NÃO muda com claro/escuro.
   *
   * Ele já é escuro nos dois: a entrada é um limiar, e o cartão recortado
   * sobre um campo fundo tem foco que um cartão claro sobre fundo claro não
   * tem. Deixar o acento entrar aqui foi o erro da primeira versão — no modo
   * escuro `--t-p` vira a variante CLARA, e o fundo clareava justo onde
   * deveria estar mais fundo.
   */
  r.setProperty('--t-login-from', theme.loginFrom);
  r.setProperty('--t-login-to',   theme.loginTo);

  document.documentElement.dataset.theme = dark ? 'dark' : 'light';
}

interface ThemePickerProps {
  currentThemeId: string;
  onSelect: (theme: Theme) => void;
  dark: boolean;
  onToggleDark: (dark: boolean) => void;
}

const ThemePicker: React.FC<ThemePickerProps> = ({ currentThemeId, onSelect, dark, onToggleDark }) => {
  const [open, setOpen] = useState(false);
  const current = THEMES.find(t => t.id === currentThemeId) ?? THEMES[0];

  /*
   * Aqui havia um `<div className="tp-backdrop">` com `onClick` para fechar.
   * Ele é `fixed; inset: 0`, mas `.app-nav` tem `backdrop-filter` e isso faz da
   * barra o bloco de contenção dos `fixed` de dentro — o fundo saía com 390x51
   * em vez de 390x844. Cobria a própria barra e mais nada.
   */
  const raiz = useRef<HTMLDivElement>(null);
  useFecharAoClicarFora(raiz, open, () => setOpen(false));
  useFechavelComVoltar(open, React.useCallback(() => setOpen(false), []));

  return (
    <div className="tp-root" ref={raiz}>
      <button
        className="tp-trigger"
        style={{ background: current.p }}
        onClick={() => setOpen(v => !v)}
        title="Mudar cor do tema"
      />

      {open && (
        <>
          <div className="tp-popover">
            <p className="tp-popover-label">Cor do tema</p>
            <div className="tp-swatches">
              {THEMES.map(t => (
                <button
                  key={t.id}
                  className={`tp-swatch${t.id === currentThemeId ? ' tp-swatch-active' : ''}`}
                  style={{ background: `linear-gradient(135deg, ${t.p}, ${t.pb})` }}
                  title={t.name}
                  onClick={() => { onSelect(t); setOpen(false); }}
                />
              ))}
            </div>

            {/* Claro/escuro mora aqui e não num botão à parte: é a mesma
                decisão que a cor — "como o app se parece" — e separar faria
                duas paradas para uma escolha só. */}
            <div className="tp-modo">
              <button className={`tp-modo-opcao${!dark ? ' ativo' : ''}`}
                      onClick={() => onToggleDark(false)} aria-pressed={!dark}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <circle cx="12" cy="12" r="4" />
                  <path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
                </svg>
                Claro
              </button>
              <button className={`tp-modo-opcao${dark ? ' ativo' : ''}`}
                      onClick={() => onToggleDark(true)} aria-pressed={dark}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="2" strokeLinejoin="round">
                  <path d="M20 14.5A8.5 8.5 0 019.5 4a8.5 8.5 0 1010.5 10.5z" />
                </svg>
                Escuro
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ThemePicker;
